/*
 * SCR-010 Generate Looks controller — Phase A1. The HOST of <nc-fitting-room> (preset "workspace"). Reads and writes only through
 * NcApp.gateway() (the mock in A1): searchProducts (chooser, categories from its facets, pagination), getProduct, listLooks,
 * getAsset, setFavorite, setLookLike, setLookSaved. No network of its own, no persistence, no images of its own.
 *
 * The look model (changeLook, planOps, rollbackOps, history, undo / redo / clear, canvas + list + total + chips sync, rollback on a
 * gateway refusal, save, share, add) moved to assets/nc-fitting-room.js unchanged in behaviour. Here the chooser chip calls
 * room.toggle(product, on) and the chips follow the component's `nc-fitting:change` event, so there is still ONE model (AC-006).
 * Each async region (products, community) has its own skeleton, empty or error state with "Reintentar" (AC-012); request
 * sequences guard against stale responses. Garment capacity is the gateway's (assets/nc-tryon-config.js): its refusal rolls the add
 * back and the toast shows its message.
 * UX audit 2026-10-24: below 700 px of the fitting room the chooser is a carousel right under the canvas, so a page holds the WHOLE
 * category (no pagination); the first hero benefit is filled with the daily look quota ('{max}', from the try-on config / quota event).
 * Navigation is Phase B: events `nc-generate-looks:navigate` {id}, `:open` {kind, id}, `:model`, `:saved` {lookId}, `:shared`
 * {lookId, url}; the last three are re-emitted from nc-fitting:model / :save / :share. Pure helpers are exported for node:test.
 */
(function () {
  // the product page of a garment through the route map (NcNav, /products/<handle>); '' when there is none
  function productHref(id) {
    try { return (typeof window !== 'undefined' && window.NcNav && window.NcNav.url && window.NcNav.url('product', { product: id })) || ''; } catch (e) { return ''; }
  }
  function isRealHref(href) { return !!href && href !== '#' && href.charAt(0) !== '#'; }
  function pageCount(total, size) {
    var t = Math.max(0, Math.floor(total) || 0);
    var s = Math.max(1, Math.floor(size) || 1);
    return Math.max(1, Math.ceil(t / s));
  }

  // label of a body-slot category chip: labels are the section's, in the order of the slot map (assets/nc-tryon-config.js SLOT_ORDER)
  function slotLabel(order, labels, value) {
    var at = (order || []).indexOf(value);
    return at >= 0 && labels && labels[at] ? labels[at] : '';
  }

  var Core = typeof module !== 'undefined' && module.exports ? require('./nc-fitting-room.js') : window.NcFittingRoomCore;

  // the garment of the look that sits in the same body slot as `product` (a different garment), or null: its chooser button says "Cambiar".
  // The chip itself (copy, pressed, busy, toggle) is the shared chooser path of the component core (syncChooserChips / toggleChooserChip).
  function slotOccupant(look, product) { return Core.slotClash(look || [], product); }

  // "Prueba hasta {max} looks al día": the pattern with the quota, or '' while the quota is unknown
  function quotaBenefit(pattern, max) {
    var n = parseInt(max, 10);
    if (String(pattern || '').indexOf('{max}') < 0) return String(pattern || '');
    return n > 0 ? fill(pattern, { max: n }) : '';
  }

  // gateway filters for a category chip ('' = all)
  function categoryFilters(value) { return value ? { category: [value] } : {}; }

  // only "más populares" (index 2) reorders (likes); the others keep the gateway order
  function sortLooks(list, index) {
    var copy = list.slice();
    if (index === 2) copy.sort(function (a, b) { return (b.likes || 0) - (a.likes || 0); });
    return copy;
  }

  var fill = Core.fill;
  var CAROUSEL_BELOW = 700; // width of the fitting room under which the chooser is the carousel (assets/section-nc-generate-looks.css)
  var errorCopy = Core.errorCopy;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { pageCount: pageCount, categoryFilters: categoryFilters, sortLooks: sortLooks, slotLabel: slotLabel, slotOccupant: slotOccupant, quotaBenefit: quotaBenefit, CAROUSEL_BELOW: CAROUSEL_BELOW };
  }
  if (typeof customElements === 'undefined' || customElements.get('nc-generate-looks-screen')) return;

  function noop() {}

  class NcGenerateLooksScreen extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.gw = window.NcApp.gateway();
      this.catalog = {};
      this.category = '';
      this.page = 1;
      this.sort = 0;
      this.categoriesDone = false;
      this.productCache = {};
      this.sizes = (this.getAttribute('data-page-sizes') || '6|8|6').split('|').map(function (n) { return parseInt(n, 10) || 6; });
      this.reqProducts = 0;
      this.reqLooks = 0;
      this.slotLabels = this.list('data-slot-labels');
      this.slotMsg = this.list('data-msg-slots');
      this.m = { products: this.list('data-msg-products'), messages: this.list('data-msg-messages'), state: this.list('data-msg-state'), errors: this.list('data-msg-errors') };
      this.room = this.querySelector('nc-fitting-room');
      this.pager = this.querySelector('nc-pagination');
      this.statusEl = this.querySelector('[data-gl-status]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('nc-chip:toggle', this.onChip.bind(this));
      // the component's events become this screen's Phase B hooks (cancelable ones pass preventDefault back)
      this.addEventListener('nc-fitting:change', function () { self.syncChips(); });
      this.addEventListener('nc-fitting:save', function (e) { self.relay('saved', { lookId: e.detail.lookId }); });
      this.addEventListener('nc-fitting:share', function (e) { self.relay('shared', { lookId: e.detail.lookId, url: e.detail.url }); });
      this.addEventListener('nc-fitting:model', function (e) { if (!self.relay('model', e.detail, true)) e.preventDefault(); });
      this.addEventListener('nc-fitting:quota', function (e) { if (e.detail && e.detail.daily) self.paintQuotaBenefit(e.detail.daily); });
      this.paintQuotaBenefit(window.NcTryonConfig && window.NcTryonConfig.dailyLookQuota);
      // A10: "Mis looks" is the gallery of this page; its link carries the number of generated looks
      this.addEventListener('nc-looks:change', function (e) { var c = self.q('[data-gl-looks-count]'); if (c) c.textContent = e.detail && e.detail.count ? '(' + e.detail.count + ')' : ''; });
      this.addEventListener('nc-card:toggle', this.onToggle.bind(this));
      this.addEventListener('nc-pagination:change', function (e) { self.page = e.detail.page; self.showProductsPage(true); });
      // the products per page follow the layout (3x2 desktop): re-slice when the breakpoint changes
      ['(min-width: 1200px)', '(min-width: 768px)'].forEach(function (q) {
        var mq = window.matchMedia(q);
        var again = function () { if (self.productList && self.lastSize !== self.pageSize()) self.showProductsPage(false); };
        if (mq.addEventListener) mq.addEventListener('change', again); else if (mq.addListener) mq.addListener(again);
      });
      // ... and the carousel (whole category) follows the width of the fitting room itself
      var box = this.room && this.room.querySelector('.nc-fr__box');
      if (box && typeof ResizeObserver === 'function') {
        new ResizeObserver(function () { if (self.productList && self.lastSize !== self.pageSize()) self.showProductsPage(false); }).observe(box);
      }
      Promise.all(['nc-fitting-room', 'nc-pagination', 'nc-chip'].map(function (n) { return customElements.whenDefined(n); })).then(function () {
        self.loadProducts(false);
        self.loadLooks();
      });
    }

    list(attr) { return (this.getAttribute(attr) || '').split('|').map(function (x) { return x.trim(); }); }

    q(sel) { return this.querySelector(sel); }

    msg(i) { return this.m.messages[i] || ''; }

    relay(name, detail, cancelable) {
      return this.dispatchEvent(new CustomEvent('nc-generate-looks:' + name, { bubbles: true, cancelable: !!cancelable, detail: detail || {} }));
    }

    announce(text) { if (this.statusEl) this.statusEl.textContent = text; }

    toast(message, tone) { if (window.NcToast) window.NcToast.show({ tone: tone || 'error', message: message }); }

    // images enter only through the gateway's asset records; no src (placeholder) leaves the neutral slot
    applyAsset(slot, assetId, alt, eager) {
      if (!slot || !assetId || !this.gw.getAsset) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !slot.isConnected) return;
        var img = document.createElement('img');
        img.src = a.src;
        // F11: the cards are 136 px wide but the catalogue prints 1200 px images: let the browser pick the width (Shopify CDN `width=`)
        if (/[?&]width=\d+/.test(a.src) && a.src.indexOf('/cdn/shop/') >= 0) {
          img.srcset = [240, 480, 720].map(function (w) { return a.src.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w'; }).join(', ');
          img.sizes = Math.max(slot.clientWidth || 0, 96) + 'px';
        }
        if (a.width) img.width = a.width;
        if (a.height) img.height = a.height;
        img.alt = a.alt || alt || '';
        img.loading = eager ? 'eager' : 'lazy';
        slot.insertBefore(img, slot.firstChild);
      }, noop);
    }

    showBlock(attr, root, name) {
      Array.prototype.forEach.call(root.querySelectorAll('[' + attr + ']'), function (el) { el.hidden = el.getAttribute(attr) !== name; });
    }

    // ---- products: chooser with categories and client-side pagination of the whole category ----
    isCarousel() {
      var box = this.room && this.room.querySelector('.nc-fr__box');
      return !!box && box.getBoundingClientRect().width > 0 && box.getBoundingClientRect().width < CAROUSEL_BELOW;
    }

    // the hero benefit "Prueba hasta {max} looks al día" (kept invisible, without layout shift, until the quota is known)
    paintQuotaBenefit(max) {
      var el = this.q('[data-gl-quota-benefit]');
      if (!el) return;
      var text = quotaBenefit(el.getAttribute('data-pattern'), max);
      if (!text) return;
      el.textContent = text;
      var li = el.closest('[data-gl-quota-pending]');
      if (li) li.removeAttribute('data-gl-quota-pending');
    }

    pageSize() {
      if (this.isCarousel()) return Math.max(1, (this.productList || []).length);
      if (window.matchMedia('(min-width: 1200px)').matches) return this.sizes[0];
      return window.matchMedia('(min-width: 768px)').matches ? this.sizes[1] : this.sizes[2];
    }

    fetchProducts(category) {
      var self = this;
      var all = [];
      var facets = null;
      var next = function (n) {
        // groupBy 'slot': the categories are the body slots of the garments (assets/nc-tryon-config.js); a gateway that does not know it answers as before
        return self.gw.searchProducts({ query: '', filters: categoryFilters(category), groupBy: 'slot', page: n }).then(function (res) {
          if (!facets) facets = res;
          var items = res.items || [];
          all = all.concat(items);
          if (items.length && all.length < (res.total || 0) && n < 50) return next(n + 1);
          return { items: all, facets: facets.facets };
        });
      };
      return next(1);
    }

    loadProducts(focusHeading, refresh) {
      var self = this;
      var seq = ++this.reqProducts;
      var panel = this.q('[data-gl-products-panel]');
      this.showBlock('data-gl-block', panel, 'skeleton');
      var key = this.category;
      if (refresh) delete this.productCache[key];
      var job = this.productCache[key] ? Promise.resolve(this.productCache[key]) : this.fetchProducts(key);
      job.then(function (res) {
        if (seq !== self.reqProducts) return;
        self.productCache[key] = res;
        if (!self.categoriesDone) self.renderCategories((res.facets && res.facets.category) || []);
        self.productList = res.items;
        if (!res.items.length) {
          self.showBlock('data-gl-block', panel, 'empty');
          self.announce(self.m.products[7]);
          return;
        }
        self.showProductsPage(focusHeading);
      }, function (err) {
        if (seq !== self.reqProducts) return;
        self.productList = null;
        var auth = err && err.kind === 'unauthenticated';
        self.showBlock('data-gl-block', panel, auth ? 'auth' : 'error');
        var block = panel.querySelector('[data-gl-block="' + (auth ? 'auth' : 'error') + '"] [role="alert"], [data-gl-block="' + (auth ? 'auth' : 'error') + '"] [role="status"]');
        self.applyErrorCopy(block, err);
        if (block) { block.setAttribute('tabindex', '-1'); block.focus({ preventScroll: true }); }
      });
    }

    // renders the current page of the loaded category: size by layout, pagination from the whole list
    showProductsPage(focusHeading) {
      var panel = this.q('[data-gl-products-panel]');
      var size = this.pageSize();
      var pages = pageCount(this.productList.length, size);
      this.lastSize = size;
      this.page = Math.max(1, Math.min(this.page, pages));
      this.renderGrid(this.productList.slice((this.page - 1) * size, this.page * size));
      this.pager.setPage(this.page, pages);
      this.showBlock('data-gl-block', panel, 'content');
      this.syncChips();
      if (focusHeading) { var h = this.q('#gl-products-title'); if (h) h.focus({ preventScroll: false }); }
    }

    // error panel copy by failure kind (403, 409, 5xx, sign-in); network keeps the section's default copy
    applyErrorCopy(panel, err) {
      if (!panel) return;
      var title = panel.querySelector('.nc-state-panel__title');
      var body = panel.querySelector('.nc-state-panel__body');
      if (!title || !body) return;
      if (title.getAttribute('data-default') === null) { title.setAttribute('data-default', title.textContent); body.setAttribute('data-default', body.textContent); }
      var copy = errorCopy(err && err.kind, this.m.errors);
      title.textContent = copy ? copy.title : title.getAttribute('data-default');
      body.textContent = copy ? copy.body : body.getAttribute('data-default');
    }

    slotOrder() { return window.NcTryonConfig && window.NcTryonConfig.SLOT_ORDER ? window.NcTryonConfig.SLOT_ORDER : []; }

    renderCategories(facets) {
      var host = this.q('[data-gl-categories]');
      var tpl = this.q('template[data-gl-tpl="category"]');
      var self = this;
      host.textContent = '';
      [{ value: '', label: this.m.products[2] }].concat(facets).forEach(function (c) {
        var chip = tpl.content.querySelector('nc-chip').cloneNode(true);
        chip.setAttribute('data-value', c.value);
        var main = chip.querySelector('.nc-chip__main');
        main.textContent = slotLabel(self.slotOrder(), self.slotLabels, c.value) || c.label;
        main.setAttribute('aria-pressed', c.value === self.category ? 'true' : 'false');
        host.appendChild(chip);
      });
      this.categoriesDone = true;
    }

    renderGrid(items) {
      var self = this;
      var grid = this.q('[data-gl-grid]');
      var tpl = this.q('template[data-gl-tpl="product"]');
      grid.textContent = '';
      items.forEach(function (p, i) {
        self.catalog[p.id] = { id: p.id, title: p.title, brand: p.brand, price: p.price, assetId: p.assetId, slot: p.slot };
        var li = tpl.content.querySelector('li').cloneNode(true);
        var card = li.querySelector('.nc-product-card');
        card.setAttribute('data-id', p.id);
        card.querySelector('.nc-product-card__brand').textContent = p.brand;
        // analytics: the card carries its brand slug so item_impression has a brand (a card without it biases M04 and M12)
        if ((p.brandInfo && p.brandInfo.slug) || p.brandSlug) card.setAttribute('data-nc-brand', (p.brandInfo && p.brandInfo.slug) || p.brandSlug);
        var link = card.querySelector('.nc-card__link');
        link.textContent = p.title;
        link.setAttribute('data-kind', 'product');
        link.setAttribute('data-id', p.id);
        var pHref = productHref(p.id);
        if (pHref) link.setAttribute('href', pHref);
        card.querySelector('.nc-product-card__price').textContent = p.price;
        var fav = card.querySelector('.nc-toggle--favorite');
        fav.setAttribute('data-id', p.id);
        fav.setAttribute('aria-pressed', p.favorite ? 'true' : 'false');
        fav.setAttribute('aria-label', (fav.getAttribute('aria-label') || '').replace('__TITLE__', p.title));
        var swatches = Array.prototype.slice.call(card.querySelectorAll('.nc-product-card__swatch'));
        swatches.forEach(function (sw, k) { if (p.colors && p.colors[k]) sw.style.background = p.colors[k]; else sw.remove(); });
        var wrap = card.querySelector('.nc-product-card__swatches');
        if (wrap && !wrap.children.length) wrap.remove();
        var chip = li.querySelector('nc-chip');
        chip.setAttribute('data-value', p.id);
        chip.querySelector('.nc-chip__main').setAttribute('aria-label', fill(self.m.products[16], { name: p.title }));
        grid.appendChild(li);
        self.applyAsset(card.querySelector('.nc-card__media'), p.assetId, p.title, i < 4);
      });
    }

    // chooser chips follow the model: pressed = in the look; one garment per body slot: a taken slot says "Cambiar" and names the garment
    syncChips() {
      Core.syncChooserChips(this.querySelectorAll('[data-gl-grid] nc-chip'), this.room, this.catalog, this.chipCopy());
    }

    chipCopy() {
      return { add: this.m.products[5], inLook: this.m.products[6], change: this.slotCopy(3), addAria: this.m.products[16], changeAria: this.slotCopy(4) };
    }

    slotCopy(i) { return this.slotMsg[i] || ['', '', '', 'Cambiar', 'Cambiar «{old}» por «{name}»'][i]; }

    // ---- entry points ----
    onChip(event) {
      var chip = event.target.closest('nc-chip');
      if (!chip) return;
      if (chip.closest('[data-gl-grid]')) {
        Core.toggleChooserChip(this.room, this.catalog, chip, event.detail.pressed);
        this.syncChips();
        return;
      }
      if (chip.closest('[data-gl-categories]')) {
        var value = chip.getAttribute('data-value');
        Array.prototype.forEach.call(this.querySelectorAll('[data-gl-categories] nc-chip'), function (c) {
          c.querySelector('.nc-chip__main').setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        if (value === this.category) return;
        this.category = value;
        this.page = 1;
        this.loadProducts(false);
        return;
      }
      if (chip.closest('[data-gl-sorts]')) {
        Array.prototype.forEach.call(this.querySelectorAll('[data-gl-sorts] nc-chip'), function (c) {
          c.querySelector('.nc-chip__main').setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        this.sort = parseInt(chip.getAttribute('data-value'), 10) || 0;
        if (this.lookItems) this.renderLooks();
      }
    }

    // ---- community looks ----
    loadLooks() {
      var self = this;
      var seq = ++this.reqLooks;
      var host = this.q('.nc-genlooks__community');
      this.showBlock('data-gl-looks-block', host, 'skeleton');
      this.gw.listLooks({}).then(function (res) {
        if (seq !== self.reqLooks) return;
        self.lookItems = res.items || [];
        if (!self.lookItems.length) { self.showBlock('data-gl-looks-block', host, 'empty'); return; }
        self.renderLooks();
        self.showBlock('data-gl-looks-block', host, 'content');
      }, function (err) {
        if (seq !== self.reqLooks) return;
        var auth = err && err.kind === 'unauthenticated';
        self.showBlock('data-gl-looks-block', host, auth ? 'auth' : 'error');
        self.applyErrorCopy(host.querySelector('[data-gl-looks-block="' + (auth ? 'auth' : 'error') + '"] .nc-state-panel'), err);
      });
    }

    renderLooks() {
      var self = this;
      var lgrid = this.q('[data-gl-looks]');
      var ltpl = this.q('template[data-gl-tpl="look"]');
      lgrid.textContent = '';
      sortLooks(this.lookItems, this.sort).forEach(function (l, i) {
        var li = ltpl.content.querySelector('li').cloneNode(true);
        var card = li.querySelector('.nc-community-card');
        var link = card.querySelector('.nc-card__link');
        link.textContent = l.author;
        link.setAttribute('data-kind', 'look');
        link.setAttribute('data-id', l.id);
        card.querySelector('.nc-community-card__tags').textContent = (l.tags || []).join(' ');
        // the shared card ships "__HANDLE__" in every accessible name: use the author, like the community feed
        Array.prototype.forEach.call(card.querySelectorAll('[aria-label]'), function (el) { el.setAttribute('aria-label', el.getAttribute('aria-label').replace('__HANDLE__', l.author || '')); });
        var like = card.querySelector('.nc-toggle--like');
        like.setAttribute('data-id', l.id);
        like.setAttribute('data-count', String(l.likes));
        like.setAttribute('data-initial-pressed', l.liked ? 'true' : 'false');
        like.setAttribute('aria-pressed', l.liked ? 'true' : 'false');
        var count = like.querySelector('[data-nc-count]');
        if (count) count.textContent = String(l.likes);
        var save = card.querySelector('.nc-toggle--save');
        save.setAttribute('data-id', l.id);
        save.setAttribute('aria-pressed', l.saved ? 'true' : 'false');
        lgrid.appendChild(li);
        self.applyAsset(card.querySelector('.nc-card__media'), l.assetId, l.title, false);
      });
    }

    // ---- toggles on cards (favorite, like, save) ----
    onToggle(event) {
      var d = event.detail;
      var btn = event.target.closest('.nc-toggle');
      if (!btn) return;
      var self = this;
      var call = d.kind === 'favorite' ? this.gw.setFavorite({ entity: 'product', id: d.id, value: d.pressed })
        : d.kind === 'like' ? this.gw.setLookLike({ lookId: d.id, value: d.pressed })
        : d.kind === 'save' ? this.gw.setLookSaved({ lookId: d.id, value: d.pressed }) : null;
      if (!call) return;
      window.NcCard.setState(btn, 'loading');
      call.then(function () { window.NcCard.setState(btn, 'default'); }, function () {
        window.NcCard.setState(btn, 'error', d.kind === 'favorite' ? self.msg(2) : self.msg(3));
      });
    }

    // ---- clicks: retry panels, tools, navigation events ----
    onClick(event) {
      var t = event.target;
      var retry = t.closest('[data-nc-retry]');
      if (retry) {
        var productsBlock = retry.closest('[data-gl-block]');
        if (productsBlock) {
          if (productsBlock.getAttribute('data-gl-block') === 'empty' && this.category) {
            this.category = '';
            this.page = 1;
            Array.prototype.forEach.call(this.querySelectorAll('[data-gl-categories] nc-chip'), function (c) {
              c.querySelector('.nc-chip__main').setAttribute('aria-pressed', c.getAttribute('data-value') === '' ? 'true' : 'false');
            });
          }
          this.loadProducts(false, true);
          return;
        }
        if (retry.closest('[data-gl-looks-block]')) { this.loadLooks(); return; }
      }
      var looksLink = t.closest('[data-gl-looks-link]');
      if (looksLink) {
        // desktop (§4.2): the gallery is the thumbnails column of the stage, so the link goes to the stage (the viewer when there are looks)
        var gallery = this.q('[data-looks-gallery]');
        if (gallery && !gallery.getClientRects().length) gallery = this.q('[data-fr-viewer-host]:not([hidden]) [data-looks-viewer]') || this.q('.nc-fr__stage');
        if (gallery) {
          event.preventDefault();
          var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          gallery.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
          if (!gallery.hasAttribute('tabindex')) gallery.setAttribute('tabindex', '-1');
          gallery.focus({ preventScroll: true });
        }
        return;
      }
      var nav = t.closest('[data-gl-nav]');
      if (nav) {
        if ((nav.getAttribute('href') || '#') === '#') event.preventDefault();
        this.dispatchEvent(new CustomEvent('nc-generate-looks:navigate', { bubbles: true, detail: { id: nav.getAttribute('data-gl-nav') } }));
        return;
      }
      var link = t.closest('.nc-card__link');
      if (link) {
        // a garment card OPENS its product page (Javi 2026-10-31: tapping a garment must not put it in the look); only the
        // "Probar" chip on the card adds it. A real href navigates natively; otherwise the route map resolves it from the event.
        if (link.getAttribute('data-kind') === 'product' && isRealHref(link.getAttribute('href'))) return;
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('nc-generate-looks:open', { bubbles: true, detail: { kind: link.getAttribute('data-kind'), id: link.getAttribute('data-id') } }));
      }
    }

  }

  customElements.define('nc-generate-looks-screen', NcGenerateLooksScreen);
})();
